import { useEffect, useRef, useState } from 'react';
import CryptoJS from 'crypto-js';
import MobileHeader from '../../layout/header/mobileHeader';
import WebHeader from '../../layout/header/webHeader';
import { COLORS } from '../../utils/colors';
import {
  Box,
  HStack,
  Text,
  useMediaQuery,
  VStack,
  Input,
  Pressable,
  Badge
} from 'native-base';
import { MOBILE_VIEW_WIDTH } from '../../utils/screens';
import CommonHeading from '../../components/headings';
import { addIconSVG } from '../../utils/svg';
import { filterSVG } from '../../utils/svg';
import Svg from 'react-native-svg';
import CustomButton from '../../components/button';
import HotelDetails from '../../containers/hotelDetails';
import { COMMONFONTSIZE } from '../../utils/fonts';
import MobileModelSheet from '../../components/actionSheet';
import config from '../../utils/config';
import { useSelector } from 'react-redux';
import CommonAvatar from '../../components/avatar';
import QuickMeetModal from './modalChildren/quickMeetModal';
import { Toaster } from '../../components/toaster';
import { addHotelDetailsAPI, getHotelListAPI } from '../../api/apis/quicklinks';
import { LoaderAnimation } from '../../components/spinner';
import CustomModal from '../../components/modal';
import BulkUploadModal from './modalChildren/bulkUploadModal';
import NoDataFound from '../../components/noDataFound';

const QuickMeetListingPage = ({
  setPagination = () => {},
  setEvents = () => {},
  setCurrentPage = () => {},
  hotelList = [],
  setHotelList = () => {}
}) => {
  const childref = useRef();
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });
  const activePackagesTab = useSelector((state) => {
    return state.event.activePackagesTab;
  });
  const [tabActiveButton, setTabActiveButton] = useState(activePackagesTab);
  const bytes = CryptoJS.AES.decrypt(
    sessionStorage.getItem('content'),
    config.userKey
  );
  const jsonString = bytes.toString(CryptoJS.enc.Utf8);
  const userInfo = JSON.parse(jsonString);
  const [activeButton, setActiveButton] = useState(
    userInfo.role === 'employee' ||
      userInfo.role === 'admin' ||
      (userInfo.role === 'initiator' && MobileWindow)
      ? 'package_approved'
      : activePackagesTab === 'Ongoing package'
        ? 'package_approved'
        : 'pending'
  );

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    hotel_name: '',
    city: '',
    number_of_participants: '',
    rate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    hotel_name: '',
    city: '',
    number_of_participants: '',
    rate: ''
  });
  const [filteredHotelList, setFilteredHotelList] = useState(hotelList);
  const [isSearching, setIsSearching] = useState(false);
  const [showMeetModal, setShowMeetModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPageState] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPaginationState] = useState(null);

  // Function to refresh hotel list
  const refreshHotelList = async (page = 1, per_page = 10, clearParams = false) => {
    try {
      LoaderAnimation.show();
      const response = await getHotelListAPI(
        clearParams ? '' : appliedFilters.hotel_name,
        clearParams ? '' : appliedFilters.city,
        clearParams ? '' : appliedFilters.number_of_participants,
        clearParams ? '' : appliedFilters.rate,
        clearParams ? '' : (searchQuery.trim() || ''),
        page,
        per_page
      );

      if (response.error) {
        console.error('Failed to fetch hotels:', response.message);
      } else {
        // Handle different possible response structures
        let hotelData = [];
        let pagination = null;
        
        if (response.data?.data?.data?.hotel_data) {
          // Structure: response.data.data.data.hotel_data
          hotelData = response.data.data.data.hotel_data;
          pagination = response.data.data.pagination;
        } else if (response.data?.data?.hotel_data) {
          // Structure: response.data.data.hotel_data
          hotelData = response.data.data.hotel_data;
          pagination = response.data.data.pagination;
        } else if (response.data?.hotel_data) {
          // Structure: response.data.hotel_data
          hotelData = response.data.hotel_data;
          pagination = response.data.pagination;
        } else if (response.pagination) {
          // Structure: response.data.hotel_data with pagination at root level
          hotelData = response.data?.hotel_data || [];
          pagination = response.pagination;
        }
        
        if (page === 1) {
          // First page - replace the list
          setFilteredHotelList(hotelData);
          setHotelList(hotelData);
        } else {
          // Subsequent pages - append to the list
          setFilteredHotelList(prev => [...prev, ...hotelData]);
          setHotelList(prev => [...prev, ...hotelData]);
        }
        
        // Update pagination state if needed
        if (pagination) {
          setPaginationState(pagination);
          // Only show "Load More" if there are more pages AND there are hotels in the current response
          setHasMore(pagination.page < pagination.pages && hotelData.length > 0);
        } else {
          // If no pagination info, hide "Load More" if no hotels
          setHasMore(hotelData.length > 0);
        }
      }
    } catch (error) {
      console.error('Error refreshing hotel list:', error);
    } finally {
      LoaderAnimation.hide();
    }
  };

  // Additional utility function to check if any filters are active
  const hasActiveSearchOrFilters = () => {
    return (
      searchQuery.trim() ||
      Object.values(appliedFilters).some((value) => value.trim())
    );
  };

  // Function to check if filters are applied (for badge display)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some((value) => value.trim());
  };

  // Function to get count of applied filters
  const getAppliedFiltersCount = () => {
    return Object.values(appliedFilters).filter((value) => value.trim()).length;
  };

  // Initial data load
  useEffect(() => {
    // Load initial data when component mounts
    refreshHotelList(1, 10);
  }, []);

  useEffect(() => {
    // Only reset to original list if no active search or filters
    if (!hasActiveSearchOrFilters()) {
      setFilteredHotelList(hotelList);
    }
  }, [hotelList]);

  // Handle search when Enter key is pressed
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Updated search functionality
  const handleSearch = async (
    customSearchQuery = null,
    customFilters = null
  ) => {
    setIsSearching(true);
    try {
      LoaderAnimation.show();

      const searchParam =
        customSearchQuery !== null ? customSearchQuery : searchQuery;
      const filtersToUse =
        customFilters !== null ? customFilters : appliedFilters;

      let apiParams = {
        hotel_name: filtersToUse.hotel_name.trim() || '',
        city: filtersToUse.city.trim() || '',
        number_of_participants:
          filtersToUse.number_of_participants.trim() || '',
        rate: filtersToUse.rate.trim() || ''
      };

      // Add search parameter if present from search input
      if (searchParam.trim()) {
        apiParams.search = searchParam.trim();
      }

      // Call your existing API function
      const response = await getHotelListAPI(
        apiParams.hotel_name,
        apiParams.city,
        apiParams.number_of_participants,
        apiParams.rate,
        apiParams.search || '',
        1, // page - always start with page 1 for search
        10 // per_page
      );

      if (response.error) {
        toaster(
          response.message || 'Failed to search hotels',
          'error',
          'error'
        );
      } else {
        // Handle different possible response structures
        let hotelData = [];
        let pagination = null;
        hotelData = response.data?.data?.data?.hotel_data || [];
        pagination = response.data?.data?.pagination;
        setFilteredHotelList(hotelData);
        setHotelList(hotelData);
        setCurrentPageState(1); // Reset to page 1 for new search
        
        // Only show "Load More" if there are more pages AND there are hotels in the current response
        if (pagination) {
          setPaginationState(pagination);
          setHasMore(pagination.page < pagination.pages && hotelData.length > 0);
        } else {
          setHasMore(hotelData.length > 0);
        }
        
        // Show appropriate toast message based on results
        if (hotelData.length > 0) {
          toaster(
            `Found ${pagination?.total} hotels`,
            'success',
            'success'
          );
        } else {
          toaster(
            'No hotels found matching your criteria',
            'warning',
            'warning'
          );
        }
      }
    } catch (error) {
      console.error('Error searching hotels:', error);
      toaster('Error searching hotels', 'error', 'error');
    } finally {
      setIsSearching(false);
      LoaderAnimation.hide();
    }
  };

  // Filter modal functions
  const openFilterModal = () => {
    setShowFilterModal(true);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  // Handle filter input changes
  const handleFilterChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters and search
  const applyFilters = async () => {
    // Update applied filters when apply is clicked
    setAppliedFilters(searchFilters);
    await handleSearch(searchQuery, searchFilters);
    closeFilterModal();
  };

  // Updated clear filters function - Reset to original list or call API
  const clearFilters = async () => {
    const clearedFilters = {
      hotel_name: '',
      city: '',
      number_of_participants: '',
      rate: ''
    };

    setSearchFilters(clearedFilters);
    setAppliedFilters(clearedFilters);

    // If there's still a search query, apply only the search with cleared filters
    if (searchQuery.trim()) {
      await handleSearch(searchQuery, clearedFilters);
    } else {
      // If no search query, reset to original hotel list without API call
      setFilteredHotelList(hotelList);
      setCurrentPageState(1);
      setHasMore(hotelList.length > 0);
      // Also call API to get updated pagination info with cleared params
      await refreshHotelList(1, 10, true);
    }

    closeFilterModal();
  };

  // Updated function to handle clearing search
  const handleClearSearch = async () => {
    const clearedSearchQuery = '';
    setSearchQuery(clearedSearchQuery);

    // If there are still applied filters, call API with cleared search but existing filters
    const hasActiveFilters = Object.values(appliedFilters).some((value) =>
      value.trim()
    );

    if (hasActiveFilters) {
      await handleSearch(clearedSearchQuery, appliedFilters);
    } else {
      // If no filters, reset to original hotel list
      setFilteredHotelList(hotelList);
      setCurrentPageState(1);
      setHasMore(hotelList.length > 0);
      // Also call API to get updated pagination info with cleared params
      await refreshHotelList(1, 10, true);
    }
  };

  // Clear icon SVG
  const clearIconSVG = (
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  const handleHotelSubmit = async (hotelData) => {
    try {
      LoaderAnimation.show();
      const response = await addHotelDetailsAPI(hotelData);
      if (response.error) {
        toaster(
          response.message || 'Failed to add hotel details',
          'error',
          'error'
        );
      } else {
        toaster(
          response.data.data.message || 'Hotel details added successfully',
          'success',
          'success'
        );
        setShowMeetModal(false);
        // Refresh the hotel list after successful submission
        setTimeout(async () => {
          await refreshHotelList();
          setRefreshTrigger((prev) => prev + 1);
        }, 300); // 300ms delay to allow modal animation to complete
      }
    } catch (error) {
      console.error('Error adding hotel details:', error);
      toaster('Error adding hotel details', 'error', 'error');
    } finally {
      LoaderAnimation.hide();
    }
  };

  // Load more hotels function
  const handleLoadMore = async () => {
    try {
      const nextPage = currentPage + 1;
      await refreshHotelList(nextPage, perPage);
      setCurrentPageState(nextPage);
    } catch (error) {
      console.error('Error loading more hotels:', error);
      toaster('Error loading more hotels', 'error', 'error');
    }
  };

  // Filter modal content with 4 input fields
  const getFilterModalChildren = () => {
    return (
      <VStack
        space={4}
        p={2}
      >
        {/* Hotel Name Filter */}
        <VStack space={1}>
          <Text
            fontWeight='medium'
            color={COLORS.PARTNERS_HEADING_COLOR}
          >
            Hotel Name
          </Text>
          <Input
            placeholder='Enter hotel name'
            value={searchFilters.hotel_name}
            onChangeText={(value) => handleFilterChange('hotel_name', value)}
            borderColor={COLORS.BORDER_COLOR}
            _focus={{ borderColor: COLORS.TABS_ACTIVE_BORDER_COLOR }}
          />
        </VStack>

        {/* City Filter */}
        <VStack space={2}>
          <Text
            fontWeight='medium'
            color={COLORS.PARTNERS_HEADING_COLOR}
          >
            City
          </Text>
          <Input
            placeholder='Enter city'
            value={searchFilters.city}
            onChangeText={(value) => handleFilterChange('city', value)}
            borderColor={COLORS.BORDER_COLOR}
            _focus={{ borderColor: COLORS.TABS_ACTIVE_BORDER_COLOR }}
          />
        </VStack>

        {/* Number of Participants Filter */}
        <VStack space={2}>
          <Text
            fontWeight='medium'
            color={COLORS.PARTNERS_HEADING_COLOR}
          >
            Capacity
          </Text>
          <Input
            placeholder='Enter capacity'
            value={searchFilters.number_of_participants}
            onChangeText={(value) =>
              handleFilterChange('number_of_participants', value)
            }
            keyboardType='numeric'
            borderColor={COLORS.BORDER_COLOR}
            _focus={{ borderColor: COLORS.TABS_ACTIVE_BORDER_COLOR }}
          />
        </VStack>

        {/* Rate Filter */}
        <VStack space={2}>
          <Text
            fontWeight='medium'
            color={COLORS.PARTNERS_HEADING_COLOR}
          >
            Per Head Rate (₹)
          </Text>
          <Input
            placeholder='Enter rate'
            value={searchFilters.rate}
            onChangeText={(value) => handleFilterChange('rate', value)}
            keyboardType='numeric'
            borderColor={COLORS.BORDER_COLOR}
            _focus={{ borderColor: COLORS.TABS_ACTIVE_BORDER_COLOR }}
          />
        </VStack>
      </VStack>
    );
  };

  // Filter modal footer with Cancel and Apply buttons
  const getFilterModalFooter = () => {
    return (
      <HStack
        mb={3}
        justifyContent={'space-between'}
        width={'100%'}
      >
        <CustomButton
          text='Clear All'
          isOutline
          bgColor={COLORS.BLACK_COLOR}
          textColor={COLORS.BLACK_COLOR}
          onPress={clearFilters}
        />
        <CustomButton
          text='Apply Filters'
          onPress={applyFilters}
          isLoading={isSearching}
        />
      </HStack>
    );
  };

  const updateDataTabChange = () => {
    setPagination(0);
    setEvents([]);
    setCurrentPageState(1);
    setPaginationState(null);
    !MobileWindow && setActiveButton('pending');
    !MobileWindow && setTabActiveButton('New request');
    setSearchQuery('');
    setSearchFilters({
      hotel_name: '',
      city: '',
      number_of_participants: '',
      rate: ''
    });
    setAppliedFilters({
      hotel_name: '',
      city: '',
      number_of_participants: '',
      rate: ''
    });
    setFilteredHotelList(hotelList);
    setShowBulkUploadModal(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Box
      bg={COLORS.THEME_BACKGROUND}
      pb={8}
      minHeight={'100vh'}
    >
      <Box
        bg={COLORS.WHITE_COLOR}
        py={1}
      >
        {MobileWindow ? (
          <MobileHeader
            updateDataTabChange={updateDataTabChange}
            showTabs={true}
          />
        ) : (
          <WebHeader updateDataTabChange={updateDataTabChange} />
        )}
      </Box>

      <VStack
        ml={!MobileWindow && 8}
        mr={!MobileWindow && 8}
        px={MobileWindow ? '25px' : '20px'}
        py='30px'
        space={5}
        minH={'60vh'}
      >
        <HStack
          justifyContent={'space-between'}
          alignItems={'center'}
          flexWrap={MobileWindow ? 'wrap' : 'nowrap'}
        >
          <CommonHeading text={'Quick Meets'} />

          <HStack
            space={4}
            alignItems={'center'}
            flexWrap={MobileWindow ? 'wrap' : 'nowrap'}
          >
            {/* Search Input with Clear Icon - Only show if there are hotels or active search/filters */}
            {(filteredHotelList?.length > 0 || searchQuery.trim() || hasAppliedFilters()) && (
              <Box position='relative'>
                <Input
                  placeholder='Search'
                  value={searchQuery}
                  onChangeText={(text) => {
                    // Only remove leading spaces, preserve trailing and middle spaces
                    const trimmedText = text?.trimStart();
                    setSearchQuery(trimmedText);
                    // If user clears the input with backspace, trigger search immediately
                    if (!trimmedText) {
                      handleClearSearch();
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  width={MobileWindow ? '200px' : '300px'}
                  border={0}
                  bg={COLORS.WHITE_COLOR}
                  _focus={{ borderColor: 'transparent' }}
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  paddingRight={searchQuery ? '40px' : '12px'}
                />
                {searchQuery && (
                  <Pressable
                    position='absolute'
                    right={2}
                    top={0}
                    bottom={0}
                    justifyContent='center'
                    alignItems='center'
                    onPress={handleClearSearch}
                    p={1}
                    borderRadius={4}
                    _pressed={{ bg: COLORS.BORDER_COLOR }}
                  >
                    <Svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      {clearIconSVG}
                    </Svg>
                  </Pressable>
                )}
              </Box>
            )}

            {/* Filter Button with Badge - Only show if there are hotels or active search/filters */}
            {(filteredHotelList?.length > 0 || searchQuery.trim() || hasAppliedFilters()) && (
              <Box position='relative'>
                <Pressable
                  onPress={openFilterModal}
                  p={2}
                  borderRadius={8}
                >
                  <Svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                  >
                    {filterSVG}
                  </Svg>
                </Pressable>
                {/* Filter Badge */}
                {hasAppliedFilters() && (
                  <Badge
                    position='absolute'
                    top={-1}
                    right={-1}
                    bg={COLORS.THEME_COLOR}
                    borderRadius='full'
                    minW='20px'
                    h='20px'
                    justifyContent='center'
                    alignItems='center'
                    _text={{
                      color: COLORS.WHITE_COLOR,
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getAppliedFiltersCount()}
                  </Badge>
                )}
              </Box>
            )}

              {/* Bulk Upload Button */}
              <CustomButton
                text={MobileWindow ? 'Upload' : 'Bulk Upload'}
                isOutline
                bgColor={COLORS.THEME_COLOR}
                textColor={COLORS.THEME_COLOR}
                onPress={() => {
                  scrollToTop();
                  setShowBulkUploadModal(true);
                }}
              />

              {/* Add Hotel Button */}
              <CommonAvatar
                width={MobileWindow ? '35px' : '50px'}
                height={MobileWindow ? '35px' : '50px'}
                bgcolor={COLORS.TABS_ACTIVE_BORDER_COLOR}
                svg={addIconSVG}
                svgwh='24'
                view='0 0 24 24'
                onPress={() => {
                  scrollToTop();
                  setShowMeetModal(true);
                }}
                touchable={true}
              />
            </HStack>
        </HStack>

        {/* Hotel List */}
        <Box>
          {filteredHotelList?.length > 0 ? (
            <HotelDetails
              refreshHotelList={refreshHotelList}
              hotelList={filteredHotelList}
              scrollToTop={scrollToTop}
              key={refreshTrigger}
              hasMore={hasMore}
              isLoadingMore={false}
              onLoadMore={handleLoadMore}
              pagination={pagination}
            />
          ) : (
            <NoDataFound dataName="hotel details" />
          )}
        </Box>
      </VStack>

      {/* Quick Meet Modal */}
      <QuickMeetModal
        isOpen={showMeetModal}
        onClose={() => setShowMeetModal(false)}
        onSubmit={handleHotelSubmit}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={async () => {
          // Refresh the hotel list after successful bulk upload
          setTimeout(async () => {
            await refreshHotelList();
            setRefreshTrigger((prev) => prev + 1);
          }, 300);
        }}
      />

      {/* Filter Modal - Fixed position like delete modal */}
      {!MobileWindow ? (
        <CustomModal
          size={'md'}
          isOpen={showFilterModal}
          onClose={closeFilterModal}
          header={'Filter'}
          body={getFilterModalChildren()}
          footer={getFilterModalFooter()}
          fixedPosition={true}
        />
      ) : (
        <MobileModelSheet
          isOpen={showFilterModal}
          onClose={closeFilterModal}
          header={'Filter Hotels'}
          body={getFilterModalChildren()}
          footer={getFilterModalFooter()}
          maxH={'80vh'}
          position='hidden'
        />
      )}
    </Box>
  );
};

export default QuickMeetListingPage;
